import _ from 'lodash';
import React__default, { useState, useRef, useEffect } from 'react';
import { addressToString } from '../../utils/address_utils.js';
import { Button } from '../atoms/Button.js';
import { TextInput } from '../atoms/TextInput.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useHorizontalSizeClass } from '../hooks/useSizeClass.js';
import { Group } from '../layouts/Group.js';
import { Stack } from '../layouts/Stack.js';
import { Dropdown } from '../molecules/Dropdown.js';
import { SearchInput } from '../molecules/SearchInput.js';
import { ConfirmAddressModal } from '../sections/ConfirmAddressModal.js';
import { AddressHandler } from '../utils/AddressHandler.js';
import './AddressInput.scss';

const MANUAL_ENTRY_VALUE = '__manual_entry__';
/* Button sizes are one step smaller than TextInput sizes at the same
   token name, so the Dropdown (which uses Button internally) needs to
   be bumped up one step to match TextInput height. */
const DROPDOWN_SIZE_FOR_INPUT = {
    small: 'medium',
    medium: 'large',
    large: 'xlarge',
};
function AddressInput({ ariaLabel, className, defaultValue, value: controlledValue, onChange, label, invalidData, placeholder = 'Start typing address', enterAddressManuallyText = 'Enter address manually', popoverHeaderText = 'Keep typing address to display results', maxSearchResults = 5, inputSpacing = '24', size = 'medium', variant, requireFullZipcode, groupingIdentifier = '', }) {
    /* -- Component State ------------------------------------------------------ */
    const [isManualMode, setIsManualMode] = useState(false);
    const [showResultsHeader, setShowResultsHeader] = useState(true);
    const prevFoundSuggestions = useRef([]);
    const [searchValue, setSearchValue] = useState('');
    const [value, setValue] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const layoutSize = useHorizontalSizeClass();
    // Track whether we're syncing from controlled prop (to avoid triggering onChange loops)
    const isSyncingFromControlledRef = useRef(false);
    // Track the last controlled value we synced to detect actual changes
    const lastSyncedControlledValueRef = useRef(undefined);
    // Handle controlled value
    const handleSetValue = (v, fromControlled = false) => {
        let newAddress = v;
        // An empty object means null
        if (Object.keys(v || {}).length === 0) {
            newAddress = null;
        }
        // Recalculate addressString if missing
        if (newAddress && !newAddress.addressString) {
            newAddress = {
                ...newAddress,
                addressString: newAddress ? addressToString(newAddress) : undefined,
            };
        }
        if (_.isEqual(newAddress, value)) {
            return;
        }
        if (newAddress && value && addressToString(newAddress) === addressToString(value)) {
            return;
        }
        // If this is a controlled value sync, mark it so we don't fire onChange
        if (fromControlled) {
            isSyncingFromControlledRef.current = true;
        }
        // If controlled value changes, update internal state
        setIsManualMode(!!newAddress);
        setSearchValue(newAddress ? addressToString(newAddress) : '');
        setValue(newAddress);
        // When syncing from controlled value, also update selectedValue to match
        // This prevents the onChange effect from treating it as a user edit
        if (fromControlled && newAddress) {
            setSelectedValue(_.cloneDeep(newAddress));
        }
    };
    useEffect(() => {
        if (controlledValue !== undefined) {
            // If controlled value is functioning as default value and is effectively empty, don't set it
            if (controlledValue && value === null && searchValue === '') {
                const s = addressToString(controlledValue)
                    .replace(/[^A-Za-z]/g, '')
                    .trim();
                if (s === '') {
                    // Controlled value is effectively empty
                    return;
                }
            }
            // Check if this is actually a different value from what we last synced
            // This prevents re-processing the same controlled value
            const controlledStr = controlledValue ? addressToString(controlledValue) : null;
            const lastSyncedStr = lastSyncedControlledValueRef.current
                ? addressToString(lastSyncedControlledValueRef.current)
                : null;
            if (controlledStr === lastSyncedStr &&
                _.isEqual(controlledValue, lastSyncedControlledValueRef.current)) {
                return;
            }
            lastSyncedControlledValueRef.current = controlledValue;
            handleSetValue(controlledValue, true);
        }
    }, [controlledValue]);
    useEffect(() => {
        if (defaultValue !== undefined) {
            const s = addressToString(defaultValue)
                .replace(/[^A-Za-z]/g, '')
                .trim();
            if (s === '') {
                // Default value is effectively empty
                return;
            }
            if (value === null && searchValue === '') {
                // Only set default value if nothing has been set yet
                handleSetValue(defaultValue, true);
            }
        }
    }, [defaultValue]);
    /* -- Address Handling ------------------------------------------------------ */
    useEffect(() => {
        // Skip firing onChange if we're syncing from controlled value
        // This prevents the infinite loop when form re-syncs after submission
        if (isSyncingFromControlledRef.current) {
            isSyncingFromControlledRef.current = false;
            return;
        }
        if (value) {
            const madeEdit = !_.isEqual(value, selectedValue);
            if (madeEdit) {
                // If they made an edit, delete lat & lng and recalculate addressString
                onChange?.({
                    ...value,
                    zip: trimZip(value?.zip || ''),
                    lat: undefined,
                    lng: undefined,
                    addressString: addressToString(value),
                }, true);
            }
            else {
                onChange?.({
                    ...value,
                    zip: trimZip(value?.zip || ''),
                }, false);
            }
        }
    }, [value]);
    /* -- Validation ------------------------------------------------------ */
    const transformedValidationErrors = (() => {
        const strErrors = {};
        if (invalidData) {
            Object.keys(invalidData).forEach((key) => {
                const val = invalidData[key];
                if (typeof val === 'string') {
                    strErrors[key] = val;
                }
                else if (val === true) {
                    strErrors[key] = 'Invalid value';
                }
            });
        }
        return {
            firstLine: strErrors.addressLineOne,
            secondLine: strErrors.addressLineTwo,
            city: strErrors.city,
            state: strErrors.state,
            zip: strErrors.zip || strErrors.zipBase || strErrors.zipExtension,
        };
    })();
    const [hasBeenChanged, setHasBeenChanged] = useState({});
    const getMissingData = (value) => {
        const missing = checkMissingData(value, requireFullZipcode);
        // Only return errors for fields that have been changed
        const filteredMissing = {};
        Object.keys(missing || {}).forEach((key) => {
            if (hasBeenChanged[key]) {
                filteredMissing[key] = missing[key];
            }
        });
        return Object.keys(filteredMissing).length > 0 ? filteredMissing : undefined;
    };
    /* -- Text Input Variant ------------------------------------------------------ */
    const isOldStyle = variant === 'old-style';
    return (React__default.createElement("div", { "data-rev-component": 'address-input', className: className, "aria-label": ariaLabel }, isManualMode ? (React__default.createElement(Stack, { itemsSpacing: inputSpacing },
        React__default.createElement(TextInput, { label: label, type: 'text', name: 'address-line-1', variant: isOldStyle ? 'old-style' : undefined, ariaLabel: ariaLabel, autocomplete: groupingIdentifier
                ? `${groupingIdentifier} street-address`
                : 'street-address', placeholder: '', isClearable: true, value: value?.addressLineOne ?? '', onChange: (newValue) => {
                setValue((prev) => ({
                    ...prev,
                    addressLineOne: newValue,
                }));
                setHasBeenChanged((prev) => ({
                    ...prev,
                    addressLineOne: true,
                }));
            }, onClear: () => {
                handleSetValue(null);
                setIsManualMode(false);
            }, size: size, hasError: transformedValidationErrors.firstLine ??
                getMissingData(value)?.addressLineOne }),
        React__default.createElement(Group, { itemsSpacing: '16', itemsAlignY: 'start', noWrap: layoutSize !== 'compact' },
            React__default.createElement(Group.Item, { grow: 1 },
                React__default.createElement(TextInput, { label: 'Company, apt, suite, unit', type: 'text', name: 'address-line-2', autocomplete: groupingIdentifier
                        ? `${groupingIdentifier} address-line2`
                        : 'adress-line2', variant: isOldStyle ? 'old-style' : undefined, placeholder: '', value: value?.addressLineTwo ?? '', onChange: (newValue) => {
                        setValue((prev) => ({
                            ...prev,
                            addressLineTwo: newValue,
                        }));
                        setHasBeenChanged((prev) => ({
                            ...prev,
                            addressLineTwo: true,
                        }));
                    }, size: size, isOptional: true, hasError: transformedValidationErrors.secondLine ??
                        getMissingData(value)?.addressLineTwo })),
            React__default.createElement(Group.Item, { grow: 1 },
                React__default.createElement(TextInput, { label: 'City', type: 'text', name: 'address-city', variant: isOldStyle ? 'old-style' : undefined, placeholder: '', autocomplete: groupingIdentifier
                        ? `${groupingIdentifier} address-level2`
                        : 'address-level2', value: value?.city ?? '', onChange: (newValue) => {
                        setValue((prev) => ({
                            ...prev,
                            city: newValue,
                        }));
                        setHasBeenChanged((prev) => ({
                            ...prev,
                            city: true,
                        }));
                    }, size: size, hasError: transformedValidationErrors.city ?? getMissingData(value)?.city }))),
        React__default.createElement(Group, { itemsSpacing: '16', itemsAlignY: 'start', noWrap: layoutSize !== 'compact' },
            React__default.createElement(Group.Item, { grow: 1 },
                React__default.createElement(Dropdown, { className: 'address-state-dropdown--rev', label: 'State', placeholder: '', name: 'state', autoComplete: groupingIdentifier
                        ? `${groupingIdentifier} address-level1`
                        : 'address-level1', buttonVariant: isOldStyle ? 'old-style-dropdown' : undefined, options: US_STATES, value: value?.state ?? '', onSelectionChange: (value) => {
                        setValue((prev) => ({
                            ...prev,
                            state: value,
                        }));
                        setHasBeenChanged((prev) => ({
                            ...prev,
                            state: true,
                        }));
                    }, size: DROPDOWN_SIZE_FOR_INPUT[size ?? 'medium'] ?? size, hasError: transformedValidationErrors.state ??
                        getMissingData(value)?.state })),
            React__default.createElement(Group.Item, { grow: 1 },
                React__default.createElement(TextInput, { label: 'Postal code', type: 'zipcode-ext', name: 'address-zip', autocomplete: groupingIdentifier
                        ? `${groupingIdentifier} postal-code`
                        : 'postal-code', variant: isOldStyle ? 'old-style' : undefined, placeholder: '', value: value?.zip ?? '', onChange: (newValue) => {
                        // Split into base and extension
                        const base = newValue.slice(0, 5);
                        const ext = newValue.length > 5 ? newValue.slice(5) : '';
                        setValue((prev) => ({
                            ...prev,
                            zip: `${base}-${ext}`,
                            zipBase: base,
                            zipExtension: ext || undefined,
                        }));
                        setHasBeenChanged((prev) => ({
                            ...prev,
                            zip: true,
                        }));
                    }, size: size, hasError: transformedValidationErrors.zip ?? getMissingData(value)?.zip }))))) : (React__default.createElement(Stack, { itemsSpacing: '12' },
        React__default.createElement(SearchInput, { label: label, type: 'search', name: 'address-search', ariaLabel: ariaLabel, placeholder: placeholder, size: size, resultsHeaderContent: showResultsHeader ? popoverHeaderText : undefined, isClearable: true, variant: isOldStyle ? 'old-style' : undefined, value: searchValue, onChange: setSearchValue, handleSearch: async (query) => {
                const suggestions = await new AddressHandler().findSuggestions(query, maxSearchResults);
                setShowResultsHeader(suggestions.length > 0);
                prevFoundSuggestions.current = suggestions;
                return [
                    ...suggestions.map((sugg) => {
                        const address = transformSuggestionToAddress(sugg);
                        return {
                            value: address.addressString ?? '',
                            label: address.addressString ?? '',
                        };
                    }),
                    {
                        value: MANUAL_ENTRY_VALUE,
                        label: enterAddressManuallyText,
                    },
                ];
            }, onChangePopoverVisibility: (isVisible) => {
                if (!isVisible) {
                    prevFoundSuggestions.current = [];
                    setShowResultsHeader(true);
                }
            }, onSelectItem: async (value) => {
                setIsManualMode(true);
                if (value === MANUAL_ENTRY_VALUE) {
                    // Fill in whatever they had typed as addressLineOne
                    const partialAddress = {
                        addressLineOne: searchValue,
                        addressLineTwo: '',
                        city: '',
                        state: '',
                        zip: '',
                        country: 'US',
                    };
                    partialAddress.addressString = addressToString(partialAddress);
                    setValue(partialAddress);
                    setSelectedValue(_.cloneDeep(partialAddress));
                }
                else {
                    // Update search value to cleaned version
                    setSearchValue(cleanAddressString(value));
                    // Converts from String -> Suggestion -> Basic Address -> Detailed Address
                    const suggestion = prevFoundSuggestions.current.find((sugg) => transformSuggestionToAddress(sugg).addressString === value);
                    if (suggestion) {
                        const basicAddress = transformSuggestionToAddress(suggestion);
                        const detailedAddress = await new AddressHandler().findDetailedAddress(basicAddress);
                        setValue(detailedAddress);
                        setSelectedValue(_.cloneDeep(detailedAddress));
                    }
                }
            } }),
        React__default.createElement(Group, { itemsAlignX: 'start' },
            React__default.createElement(Button, { className: 'address-manual-entry-button--rev', variant: 'underlined', size: 'tiny', onPress: () => setIsManualMode(true) }, enterAddressManuallyText))))));
}
/**
 * Transforms a Smarty suggestion into our Address format.
 * @param suggestion - The Smarty suggestion to transform.
 * @returns - The transformed Address object.
 */
function transformSuggestionToAddress(suggestion) {
    const address = {
        addressLineOne: suggestion.streetLine.trim(),
        addressLineTwo: suggestion.secondary.trim(),
        city: suggestion.city.trim(),
        state: suggestion.state.trim(),
        zip: suggestion.zipcode.trim(),
        country: 'US', // Implied by using the US Street API
    };
    // If there are multiple entries for the same address, don't include addressLineTwo
    if (suggestion.entries > 1) {
        address.addressLineTwo = '';
    }
    address.addressString = addressToString(address);
    return address;
}
/**
 * Checks the address for missing required fields.
 * @param address - The address to check.
 * @returns - An object with error messages for missing fields.
 */
const checkMissingData = (address, requireFullZipcode = false) => {
    const strErrors = {};
    if (address?.addressLineOne && address.addressLineOne.trim().length > 4) {
        strErrors.addressLineOne = undefined;
    }
    else {
        strErrors.addressLineOne = 'Address is required';
    }
    if (address?.city && address.city.trim().length > 2) {
        strErrors.city = undefined;
    }
    else {
        strErrors.city = 'City is required';
    }
    if (address?.state && address.state.trim().length >= 2) {
        strErrors.state = undefined;
    }
    else {
        strErrors.state = 'State is required';
    }
    const zipTrimmed = trimZip(address?.zip || '');
    if (address?.zip && (requireFullZipcode ? zipTrimmed.length >= 10 : zipTrimmed.length >= 5)) {
        strErrors.zip = undefined;
    }
    else {
        strErrors.zip = requireFullZipcode
            ? 'Zip code with 4-digit extension is required'
            : 'Zip code is required';
    }
    const hasErrors = Object.values(strErrors).some((val) => val !== undefined);
    return hasErrors ? strErrors : undefined;
};
function trimZip(zip) {
    return zip.trim().endsWith('-') ? zip.trim().slice(0, -1) : zip;
}
/**
 * Cleans the address string by removing secondary entries info.
 */
const SECONDARY_REGEX = / - \d+ Addresses$/;
function cleanAddressString(addressString) {
    return addressString.replace(SECONDARY_REGEX, '').trim();
}
// US States for Dropdown
const US_STATES = [
    { label: 'Alabama', id: 'AL' },
    { label: 'Alaska', id: 'AK' },
    { label: 'Arizona', id: 'AZ' },
    { label: 'Arkansas', id: 'AR' },
    { label: 'California', id: 'CA' },
    { label: 'Colorado', id: 'CO' },
    { label: 'Connecticut', id: 'CT' },
    { label: 'Delaware', id: 'DE' },
    { label: 'Florida', id: 'FL' },
    { label: 'Georgia', id: 'GA' },
    { label: 'Hawaii', id: 'HI' },
    { label: 'Idaho', id: 'ID' },
    { label: 'Illinois', id: 'IL' },
    { label: 'Indiana', id: 'IN' },
    { label: 'Iowa', id: 'IA' },
    { label: 'Kansas', id: 'KS' },
    { label: 'Kentucky', id: 'KY' },
    { label: 'Louisiana', id: 'LA' },
    { label: 'Maine', id: 'ME' },
    { label: 'Maryland', id: 'MD' },
    { label: 'Massachusetts', id: 'MA' },
    { label: 'Michigan', id: 'MI' },
    { label: 'Minnesota', id: 'MN' },
    { label: 'Mississippi', id: 'MS' },
    { label: 'Missouri', id: 'MO' },
    { label: 'Montana', id: 'MT' },
    { label: 'Nebraska', id: 'NE' },
    { label: 'Nevada', id: 'NV' },
    { label: 'New Hampshire', id: 'NH' },
    { label: 'New Jersey', id: 'NJ' },
    { label: 'New Mexico', id: 'NM' },
    { label: 'New York', id: 'NY' },
    { label: 'North Carolina', id: 'NC' },
    { label: 'North Dakota', id: 'ND' },
    { label: 'Ohio', id: 'OH' },
    { label: 'Oklahoma', id: 'OK' },
    { label: 'Oregon', id: 'OR' },
    { label: 'Pennsylvania', id: 'PA' },
    { label: 'Rhode Island', id: 'RI' },
    { label: 'South Carolina', id: 'SC' },
    { label: 'South Dakota', id: 'SD' },
    { label: 'Tennessee', id: 'TN' },
    { label: 'Texas', id: 'TX' },
    { label: 'Utah', id: 'UT' },
    { label: 'Vermont', id: 'VT' },
    { label: 'Virginia', id: 'VA' },
    { label: 'Washington', id: 'WA' },
    { label: 'West Virginia', id: 'WV' },
    { label: 'Wisconsin', id: 'WI' },
    { label: 'Wyoming', id: 'WY' },
];
function AddressInputDebouncedValidation(props) {
    const [_currentData, setCurrentData] = useState(null);
    const currentData = useDebouncedValue(_currentData, props.debounceMs ?? 2000);
    const [suggestedAddress, setSuggestedAddress] = useState(null);
    const [lastApprovedAddressStr, setLastApprovedAddressStr] = useState(null);
    useEffect(() => {
        if (currentData === null) {
            return;
        }
        if (currentData.address.addressString === lastApprovedAddressStr) {
            // No need to re-verify if they reverted to last approved address
            setSuggestedAddress(null);
            return;
        }
        if (currentData.edited) {
            const hasErrors = !!checkMissingData(currentData.address, props.requireFullZipcode);
            if (hasErrors) {
                return;
            }
            new AddressHandler().findDetailedAddress(currentData.address).then((suggested) => {
                if (suggested.addressString === currentData.address.addressString) {
                    // Auto-approve if suggested basically matches current
                    updateCurrentAddress(suggested, false);
                }
                else {
                    // Show modal with suggested address
                    setSuggestedAddress(suggested);
                }
            });
        }
        else {
            setLastApprovedAddressStr(currentData.address.addressString ?? null);
            setSuggestedAddress(null);
        }
    }, [currentData]);
    const updateCurrentAddress = (address, edited) => {
        props.onChange?.(address, edited, checkMissingData(address, props.requireFullZipcode));
        setCurrentData({
            address,
            edited,
        });
        setLastApprovedAddressStr(address.addressString ?? null);
        setSuggestedAddress(null);
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(AddressInput, { ...props, onChange: (address, edited) => {
                props.onChange?.(address, edited, checkMissingData(address, props.requireFullZipcode));
                setCurrentData({
                    address,
                    edited,
                });
            } }),
        React__default.createElement(ConfirmAddressModal, { isOpen: suggestedAddress !== null &&
                currentData !== null &&
                addressToString(suggestedAddress) !== addressToString(currentData.address) &&
                lastApprovedAddressStr !== addressToString(currentData.address), onClose: () => {
                setSuggestedAddress(null);
            }, oldAddress: currentData?.address, newAddress: suggestedAddress, onConfirm: (address, type) => {
                updateCurrentAddress(address, type === 'old' ? currentData.edited : false);
            } })));
}
AddressInputDebouncedValidation.displayName = 'AddressInputDebouncedValidation';
AddressInput.displayName = 'AddressInput';

export { AddressInput, AddressInputDebouncedValidation, US_STATES };
//# sourceMappingURL=AddressInput.js.map
